import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, User, Settings, Clock, ExternalLink, Pencil, Printer, Package, Cpu, Database, HardDrive } from 'lucide-react';
import QRCode from 'react-qr-code';
import { supabase } from '../../../lib/supabaseClient';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';
import PrintableAssetLabel from '../../../components/PrintableAssetLabel';

import { formatAssetStatus, formatDate } from '../../../utils/formatters';
import { usePreferences } from '../../../hooks/usePreferences';

const AssetQuickViewPanel = ({ isOpen, onClose, asset, isPic }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const { preferences } = usePreferences();
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

  useEffect(() => {
    const fetchActivities = async () => {
      if (!asset?.asset_tag || !isOpen) return;

      setIsLoadingActivities(true);
      try {
        const { data, error } = await supabase
          .from('activities')
          .select('*')
          .eq('asset_tag', asset.asset_tag)
          .order('created_at', { ascending: false })
          .limit(5);

        if (error) throw error;
        setActivities(data || []);
      } catch (err) {
        console.error('Error fetching asset activities:', err);
      } finally {
        setIsLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [asset?.asset_tag, isOpen]);

  if (!isOpen) return null;

  const handleViewFullDetails = () => {
    if (asset?.asset_tag) {
      navigate(`/assets/${asset.asset_tag}`);
      onClose();
    }
  };

  const handleEditAsset = () => {
    if (asset?.asset_tag) {
      navigate(`/asset-registration?tag=${asset.asset_tag}`);
      onClose();
    }
  };

  const handlePrintQR = () => {
    const qrElement = document.getElementById(`qr-code-${asset.asset_tag}`);
    const stickerElement = document.getElementById('print-section');
    
    if (!qrElement || !stickerElement) {
      console.error("Print elements not found in DOM");
      // Fallback to native print if elements aren't found
      window.print();
      return;
    }

    let qrContent = '';
    // Check if the QR code is a canvas. If so, convert pixels to an image!
    if (qrElement.tagName.toLowerCase() === 'canvas') {
      const dataUrl = qrElement.toDataURL('image/png');
      qrContent = `<img src="${dataUrl}" style="width: 100%; height: auto;" />`;
    } else {
      // If it is an SVG, outerHTML works perfectly
      qrContent = qrElement.outerHTML;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=800');
    if (!printWindow) {
      alert(t('asset_details.allow_popups'));
      return;
    }

    // Capture the sticker's HTML but replace the QR container with our processed content
    // This maintains the professional Panasonic ISD sticker layout
    printWindow.document.write(`
      <html>
        <head>
          <title>${t('asset_details.print_asset_label_title', { tag: asset.asset_tag })}</title>
          <style>
            body { 
              margin: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              height: 100vh;
              background-color: white;
            }
            .sticker {
              width: 50mm;
              height: 25mm;
              border: 1px solid black;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              font-family: Arial, sans-serif;
              box-sizing: border-box;
            }
            .header { font-size: 10px; font-weight: bold; margin-bottom: 4px; text-align: center; }
            .content { display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: 8px; }
            .qr-container { width: 45px; height: 45px; flex-shrink: 0; }
            .info { flex: 1; text-align: right; overflow: hidden; }
            .label { font-size: 7px; color: #666; text-transform: uppercase; font-weight: bold; display: block; line-height: 1; }
            .value { font-size: 11px; font-weight: 900; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; margin-bottom: 2px; }
            .sn-value { font-size: 9px; font-weight: 500; }
            .footer { margin-top: auto; border-top: 0.5px solid #eee; pt: 2px; }
            .footer-text { font-size: 6px; color: #999; text-align: center; margin: 2px 0 0 0; }
            svg { width: 100% !important; height: auto !important; }
          </style>
        </head>
        <body>
          <div class="sticker">
            <div class="header">PANASONIC ISD</div>
            <div class="content">
              <div class="qr-container">${qrContent}</div>
              <div class="info">
                <span class="label">${t('labels.asset_tag')}</span>
                <span class="value">${asset.asset_tag}</span>
                <span class="label">${t('labels.serial_number')}</span>
                <span class="value sn-value">${asset.serial_number || t('common.na')}</span>
              </div>
            </div>
            <div class="footer">
              <p class="footer-text">${t('asset_details.asset_management_system')}</p>
            </div>
          </div>
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 300);
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return 'bg-success/10 text-success border-success/20';
      case 'in use':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'in repair':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'broken':
        return 'bg-error/10 text-error border-error/20';
      case 'written off':
      case 'written_off':
        return 'bg-muted/10 text-muted-foreground border-border';
      case 'lost':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return parseFloat(amount).toFixed(2);
  };


  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Sticker layout for physical printing - only visible during print */}
          <PrintableAssetLabel asset={asset} />

          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100] print:hidden"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 right-0 w-full max-w-md bg-card shadow-xl flex flex-col z-[101] border-l border-border print:hidden"
          >
            {/* Header (Sticky) */}
            <div className="sticky top-0 z-10 bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t('asset_details.asset_details')}</h2>
                <p className="text-sm text-muted-foreground">{t('asset_details.quick_view')}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground hover:text-foreground"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {/* Asset Image */}
              <div className="aspect-video bg-muted rounded-xl overflow-hidden flex items-center justify-center border border-border">
                {asset?.image_url ? (
                  <AppImage
                    src={asset.image_url}
                    alt={asset.product_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center text-muted-foreground opacity-20">
                    <Package size={48} />
                    <span className="text-xs mt-2">{t('asset_details.no_image_available')}</span>
                  </div>
                )}
              </div>

              {/* Title Block */}
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-bold text-lg text-foreground leading-tight">
                    {asset?.product_name || t('asset_details.unnamed_asset')}
                  </h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusBadgeClass(asset?.status)}`}>
                    {formatAssetStatus(asset?.status)}
                  </span>
                </div>
                <div className="flex flex-col text-sm">
                  <button 
                    onClick={handleViewFullDetails}
                    className="text-primary font-medium hover:underline text-left mt-1"
                  >
                    {asset?.asset_tag}
                  </button>
                </div>
              </div>

              {/* Basic Info Grid */}
              <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                {[
                  { label: t('labels.category'), value: asset?.category || t('common.na') },
                  { label: t('labels.serial_number'), value: asset?.serial_number || t('common.na') },
                  { label: t('labels.location'), value: asset?.locations?.name || t('common.na') },
                  { label: t('labels.purchase_date'), value: formatDate(asset?.purchase_date, preferredFormat) },
                  { label: t('asset_details.purchase_cost'), value: formatCurrency(asset?.purchase_price) },
                  { label: t('asset_details.warranty'), value: asset?.warranty_months ? t('asset_details.warranty_months', { count: asset.warranty_months }) : t('common.na') },
                ].map((item, idx) => (
                  <div key={idx} className="space-y-1">
                    <p className="text-muted-foreground text-xs uppercase tracking-wider">{item.label}</p>
                    <p className="font-medium text-foreground text-sm">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Current Assignment Section */}
              <div className="pt-2">
                <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                  <User size={18} className="text-primary" />
                  {t('asset_details.current_assignment')}
                </h4>
                {(() => {
                  const activeLoan = asset?.loans?.find(l => l.status === 'active');
                  const assigneeName = activeLoan?.employees?.full_name || (activeLoan?.departments?.name ? t('asset_details.department_prefix', { department: activeLoan.departments.name }) : null);
                  
                  if (assigneeName) {
                    return (
                      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-foreground">{assigneeName}</p>
                          <p className="text-xs text-muted-foreground">
                            {activeLoan.employees?.departments?.name || activeLoan.departments?.name || t('asset_details.no_department')}
                          </p>
                        </div>
                        <div className="pt-2 border-t border-border/50 space-y-1">
                          {activeLoan.employees?.email && (
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                              <span className="font-medium">{t('labels.email')}:</span> {activeLoan.employees.email}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">{t('asset_details.assigned')}:</span> {formatDate(activeLoan.checkout_date || asset.created_at, preferredFormat)}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="bg-muted/30 rounded-lg p-4 border border-dashed border-border text-center">
                      <p className="text-sm text-muted-foreground">Unassigned</p>
                    </div>
                  );
                })()}
              </div>

              {/* Quick Specs Section */}
              <div className="pt-2">
                <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                  <Settings size={18} className="text-primary" />
                  Quick Specs
                </h4>
                {asset?.technical_specs && (asset.technical_specs.processor || asset.technical_specs.memory || asset.technical_specs.storage) ? (
                  <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
                    <p className="text-sm text-foreground flex flex-wrap gap-x-2 gap-y-1">
                      {asset.technical_specs.processor && (
                        <span className="flex items-center gap-1">
                          <Cpu size={14} className="text-muted-foreground" />
                          {asset.technical_specs.processor}
                        </span>
                      )}
                      {(asset.technical_specs.processor && (asset.technical_specs.memory || asset.technical_specs.storage)) && <span className="text-border">•</span>}
                      {asset.technical_specs.memory && (
                        <span className="flex items-center gap-1">
                          <Database size={14} className="text-muted-foreground" />
                          {asset.technical_specs.memory}
                        </span>
                      )}
                      {(asset.technical_specs.memory && asset.technical_specs.storage) && <span className="text-border">•</span>}
                      {asset.technical_specs.storage && (
                        <span className="flex items-center gap-1">
                          <HardDrive size={14} className="text-muted-foreground" />
                          {asset.technical_specs.storage}
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No quick specs available.</p>
                )}
              </div>

              {/* Recent Activity Section */}
              <div className="pt-2">
                <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-primary" />
                  Recent Activity
                </h4>
                {isLoadingActivities ? (
                  <div className="flex justify-center p-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                  </div>
                ) : activities.length > 0 ? (
                  <div className="relative pl-4 space-y-6 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-[2px] before:bg-border">
                    {activities.map((activity) => (
                      <div key={activity.id} className="relative">
                        <div className="absolute -left-[13px] top-1.5 w-3 h-3 rounded-full bg-primary border-2 border-card" />
                        <p className="text-sm text-foreground leading-tight font-bold">
                          {activity.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {activity.description}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">
                          {formatDateTime(activity.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No recent activity recorded.</p>
                )}
              </div>
            </div>

            {/* Footer (Sticky) */}
            <div className="sticky bottom-0 bg-card border-t border-border p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {!isPic && (
                  <Button 
                    variant="outline" 
                    onClick={handleEditAsset}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Pencil size={16} />
                    Edit Asset
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={handlePrintQR}
                  className={isPic ? "w-full col-span-2 flex items-center justify-center gap-2" : "w-full flex items-center justify-center gap-2"}
                >
                  <Printer size={16} />
                  Print QR
                </Button>
              </div>
              <Button 
                onClick={handleViewFullDetails}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                View Full Details
                <ExternalLink size={16} />
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AssetQuickViewPanel;
